/**
 * Weekly Review source staleness heuristics + per-project resync cooldown.
 *
 * Storage key: `rfp-weekly-review-staleness-resync-v1:{projectId}`
 */

import { differenceInCalendarDays, parseISO, isValid } from 'date-fns';
import {
  getOpportunityDaysUntilDeadline,
  type WeeklyOpportunitySearchEntity,
} from './weeklyGlobalOpportunities';

/** Expired / overdue share that marks a source as stale (when sample is large enough). */
export const WEEKLY_REVIEW_STALE_EXPIRED_RATIO = 0.6;

/** Newest published/updated older than this many days → stale. */
export const WEEKLY_REVIEW_STALE_PUBLISHED_DAYS = 21;

/** Minimum opportunities per source before applying heuristics. */
export const WEEKLY_REVIEW_STALENESS_MIN_SAMPLE = 3;

/** Cooldown between automatic portal resyncs (8h within the 6–12h band). */
export const WEEKLY_REVIEW_STALENESS_COOLDOWN_MS = 8 * 60 * 60 * 1000;

export const WEEKLY_REVIEW_STALENESS_STORAGE_PREFIX =
  'rfp-weekly-review-staleness-resync-v1';

export interface SourceStalenessStats {
  source: string;
  total: number;
  expiredCount: number;
  openCount: number;
  expiredRatio: number;
  newestActivityDaysAgo: number | null;
  isStale: boolean;
  reasons: string[];
}

export interface WeeklyReviewStalenessAssessment {
  sources: SourceStalenessStats[];
  staleSources: SourceStalenessStats[];
  isAnyStale: boolean;
}

export interface StalenessCooldownState {
  lastTriggeredAt: string;
}

function parseEntityDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const d = parseISO(value);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

function getEntityActivityDate(entity: WeeklyOpportunitySearchEntity): Date | null {
  const data = entity.data ?? {};
  return (
    parseEntityDate(data.published_date) ||
    parseEntityDate(data.publish_date) ||
    parseEntityDate(data.posted_date) ||
    parseEntityDate(entity.updated_at) ||
    parseEntityDate(entity.created_at)
  );
}

function getEntitySource(entity: WeeklyOpportunitySearchEntity): string {
  const raw = entity.data?.source;
  if (typeof raw === 'string' && raw.trim()) return raw.trim().toLowerCase();
  return 'unknown';
}

export function getWeeklyReviewStalenessStorageKey(projectId: string): string {
  return `${WEEKLY_REVIEW_STALENESS_STORAGE_PREFIX}:${projectId}`;
}

export function loadStalenessCooldownState(
  projectId: string | undefined
): StalenessCooldownState | null {
  if (!projectId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(getWeeklyReviewStalenessStorageKey(projectId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as StalenessCooldownState).lastTriggeredAt !== 'string'
    ) {
      return null;
    }
    return { lastTriggeredAt: (parsed as StalenessCooldownState).lastTriggeredAt };
  } catch {
    return null;
  }
}

export function isStalenessResyncOnCooldown(
  projectId: string | undefined,
  now: Date = new Date(),
  cooldownMs: number = WEEKLY_REVIEW_STALENESS_COOLDOWN_MS
): boolean {
  const state = loadStalenessCooldownState(projectId);
  if (!state) return false;
  const last = parseEntityDate(state.lastTriggeredAt);
  if (!last) return false;
  return now.getTime() - last.getTime() < cooldownMs;
}

export function markStalenessResyncTriggered(
  projectId: string,
  now: Date = new Date()
): void {
  if (typeof localStorage === 'undefined') return;
  const payload: StalenessCooldownState = { lastTriggeredAt: now.toISOString() };
  localStorage.setItem(getWeeklyReviewStalenessStorageKey(projectId), JSON.stringify(payload));
}

export function canTriggerStalenessResync(
  projectId: string | undefined,
  now: Date = new Date()
): boolean {
  return !!projectId && !isStalenessResyncOnCooldown(projectId, now);
}

/**
 * Assess staleness for a single portal/source from a sample of search entities.
 * Uses expired ratio and recency of published/updated activity.
 */
export function assessSourceStaleness(
  source: string,
  entities: WeeklyOpportunitySearchEntity[],
  now: Date = new Date()
): SourceStalenessStats {
  let expiredCount = 0;
  let openCount = 0;
  let newestActivity: Date | null = null;

  for (const entity of entities) {
    const days = getOpportunityDaysUntilDeadline(entity, now);
    if (days != null && days < 0) expiredCount += 1;
    else openCount += 1;

    const activity = getEntityActivityDate(entity);
    if (activity && (!newestActivity || activity > newestActivity)) {
      newestActivity = activity;
    }
  }

  const total = entities.length;
  const expiredRatio = total > 0 ? expiredCount / total : 0;
  const newestActivityDaysAgo =
    newestActivity != null ? differenceInCalendarDays(now, newestActivity) : null;

  const reasons: string[] = [];
  let isStale = false;

  if (total >= WEEKLY_REVIEW_STALENESS_MIN_SAMPLE) {
    if (expiredRatio >= WEEKLY_REVIEW_STALE_EXPIRED_RATIO) {
      isStale = true;
      reasons.push(
        `${Math.round(expiredRatio * 100)}% of sampled opportunities are already expired`
      );
    }
    if (
      newestActivityDaysAgo != null &&
      newestActivityDaysAgo >= WEEKLY_REVIEW_STALE_PUBLISHED_DAYS
    ) {
      isStale = true;
      reasons.push(
        `newest publish/update is ${newestActivityDaysAgo} days old (≥ ${WEEKLY_REVIEW_STALE_PUBLISHED_DAYS})`
      );
    }
  }

  return {
    source,
    total,
    expiredCount,
    openCount,
    expiredRatio,
    newestActivityDaysAgo,
    isStale,
    reasons,
  };
}

/** Group keyword search pools by source and assess each portal. */
export function assessWeeklyReviewStaleness(
  entities: WeeklyOpportunitySearchEntity[],
  now: Date = new Date()
): WeeklyReviewStalenessAssessment {
  const bySource = new Map<string, WeeklyOpportunitySearchEntity[]>();
  const seenIds = new Set<string>();

  for (const entity of entities) {
    const id = String(entity.id);
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    const source = getEntitySource(entity);
    const list = bySource.get(source) ?? [];
    list.push(entity);
    bySource.set(source, list);
  }

  const sources = Array.from(bySource.entries())
    .map(([source, list]) => assessSourceStaleness(source, list, now))
    .sort((a, b) => a.source.localeCompare(b.source));

  const staleSources = sources.filter((entry) => entry.isStale);

  return {
    sources,
    staleSources,
    isAnyStale: staleSources.length > 0,
  };
}

export function formatStaleSourceLabels(staleSources: SourceStalenessStats[]): string {
  return staleSources
    .map((entry) => entry.source.replace(/_/g, ' '))
    .slice(0, 6)
    .join(', ');
}
